import React, { useState, useEffect } from 'react';
import { GetLabOrders } from '../../services/OrderService';
import LabOrdersTable from './LabOrdersTable';
import CommonLoader from '../common/CommonLoader';
import { NotificationManager } from 'react-notifications';
import { orderStatusList } from "../common/statusLists";
import {
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Tooltip,
  Chip,
  Box,
  InputAdornment,
} from '@mui/material';
import { Business as BusinessIcon, Edit as EditIcon } from '@mui/icons-material';
import ClientSelectorDialog from '../common/ClientSelectorDialog';

const LabOrders = () => {
  const [loading, setLoading] = useState(false);
  const [labOrdersData, setLabOrdersData] = useState(null);
  const [labOrdersCount, setLabOrdersCount] = useState(0);
  const [clientDialogOpen, setClientDialogOpen] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);

  const [searchCriteria, setSearchCriteria] = useState({
    searchText: '',
    orderStatus: '',
    accountNumbers: '',
    startDate: '',
    endDate: '',
    pageNumber: 0,
    pageSize: 25
  });

  useEffect(() => {
    const today = new Date();
    const startDateValue = new Date(today);
    startDateValue.setDate(today.getDate() - 15);
    const endDateValue = new Date(today);
    endDateValue.setDate(today.getDate() + 15);

    const initialCriteria = {
      searchText: '',
      orderStatus: '',
      accountNumbers: '',
      startDate: startDateValue.toISOString().split('T')[0],
      endDate: endDateValue.toISOString().split('T')[0],
      pageNumber: 0,
      pageSize: 25
    };
    setSearchCriteria(initialCriteria);
    getLabOrders(initialCriteria);
  }, []);

  const getLabOrders = async (searchCriteria) => {
    try {
      setLoading(true);
      let response = await GetLabOrders(
        searchCriteria.startDate,
        searchCriteria.endDate,
        searchCriteria.orderStatus,
        searchCriteria.accountNumbers,
        searchCriteria.searchText,
        searchCriteria.pageNumber,
        searchCriteria.pageSize
      );
      setLabOrdersData(response.results);
      setLabOrdersCount(response.count);
    } catch (err) {
      NotificationManager.error('Error getting orders. ' + err, 'Error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchClick = () => {
    if (searchCriteria.startDate === '' || searchCriteria.endDate === '') {
      NotificationManager.error('Invalid Dates');
      return;
    }
    const updatedSearchCriteria = {
      ...searchCriteria,
      pageNumber: 0,
    };
    setSearchCriteria(updatedSearchCriteria);
    getLabOrders(updatedSearchCriteria);
  };

  const onChangePageSettings = async (pageNumber, pageSize) => {
    const updatedSearchCriteria = {
      ...searchCriteria,
      pageNumber: pageNumber,
      pageSize: pageSize
    };
    setSearchCriteria(updatedSearchCriteria);
    getLabOrders(updatedSearchCriteria);
  };

  // Handle status selection from dialog/dropdown
  const handleStatusSelect = (status) => {
    const currentStatuses = searchCriteria.orderStatus
      ? searchCriteria.orderStatus.split(',').filter(s => s.trim() !== '')
      : [];

    if (!currentStatuses.includes(status)) {
      const updatedStatuses = [...currentStatuses, status];
      const statusString = updatedStatuses.join(',');
      setSearchCriteria({ ...searchCriteria, orderStatus: statusString });
    }
  };

  // Remove a specific status
  const handleRemoveStatus = (status) => {
    const currentStatuses = searchCriteria.orderStatus.split(',').filter(s => s.trim() !== '');
    const updatedStatuses = currentStatuses.filter(s => s !== status);
    const statusString = updatedStatuses.join(',');
    setSearchCriteria({ ...searchCriteria, orderStatus: statusString });
  };

  // Get array of selected statuses
  const getSelectedStatuses = () => {
    if (!searchCriteria.orderStatus) return [];
    return searchCriteria.orderStatus.split(',').filter(status => status.trim() !== '');
  };

  // Handle client selection from dialog
  const handleClientSelect = (clients) => {
    if (clients && clients.length > 0) {
      setSelectedClients(clients);
      // Convert array of account numbers to comma-separated string
      const accountNumbersString = clients.map(c => c.accountNumber).join(',');
      setSearchCriteria({ ...searchCriteria, accountNumbers: accountNumbersString });
    }
  };

  // Remove a specific client
  const handleRemoveClient = (accountNumber) => {
    const updatedClients = selectedClients.filter(c => c.accountNumber !== accountNumber);
    setSelectedClients(updatedClients);
    const accountNumbersString = updatedClients.map(c => c.accountNumber).join(',');
    setSearchCriteria({ ...searchCriteria, accountNumbers: accountNumbersString });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault(); // Prevent page refresh

    if (searchCriteria.startDate === '' || searchCriteria.endDate === '') {
      NotificationManager.error('Invalid Dates');
      return;
    }

    const updatedSearchCriteria = {
      ...searchCriteria,
      pageNumber: 0,
    };

    setSearchCriteria(updatedSearchCriteria);
    getLabOrders(updatedSearchCriteria);
  };

  return (
    <div className="vlp-component-container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span className="title">Lab Orders</span>
      </div>
      <hr />

      <div className="columns">
        <div className="column">
          <form onSubmit={handleSearchSubmit}>
            <Grid container spacing={2}>

              {/* Search Text */}
              <Grid item xs={12} sm={2.4}>
                <Tooltip
                  title="Search by project category, account number, order status, client name, date received, or order number"
                  arrow
                  placement="top"
                >
                  <TextField
                    label="Search Criteria"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={searchCriteria.searchText}
                    onChange={(e) => setSearchCriteria({ ...searchCriteria, searchText: e.target.value })}
                  />
                </Tooltip>
              </Grid>

              {/* Multiple Status Selection */}
              <Grid item xs={12} sm={2.4}>
                <Tooltip
                  title="Select one or multiple statuses of the order"
                  arrow
                  placement="top"
                >
                  <FormControl variant="outlined" fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value=""
                      onChange={(e) => handleStatusSelect(e.target.value)}
                      label="Status"
                      displayEmpty
                      renderValue={() => {
                        const selectedCount = getSelectedStatuses().length;
                        return selectedCount > 0
                          ? `${selectedCount} status${selectedCount > 1 ? 'es' : ''} selected`
                          : 'Select statuses';
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select statuses</em>
                      </MenuItem>
                      {orderStatusList.map((status) => (
                        <MenuItem
                          key={status}
                          value={status}
                          disabled={getSelectedStatuses().includes(status)}
                        >
                          {status}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Tooltip>


              </Grid>

              {/* Multiple Client Account Numbers */}
              <Grid item xs={12} sm={2.4}>

                <Tooltip
                  title="Select one or multiple account numbers (clients)"
                  arrow
                  placement="top"
                >
                  <TextField
                    label="Client Account Numbers"
                    value={
                      selectedClients.length > 0
                        ? `${selectedClients.length} client${selectedClients.length > 1 ? 's' : ''} selected`
                        : ''
                    }
                    placeholder="Click to select clients"
                    size="small"
                    fullWidth
                    onClick={() => setClientDialogOpen(true)}
                    InputProps={{
                      readOnly: true,
                      style: { cursor: 'pointer' },
                      startAdornment: (
                        <InputAdornment position="start">
                          <BusinessIcon color={selectedClients.length > 0 ? "success" : "primary"} />
                        </InputAdornment>
                      ),
                      endAdornment: selectedClients.length > 0 ? (
                        <InputAdornment position="end">
                          <Tooltip title="Change clients">
                            <EditIcon color="primary" sx={{ fontSize: 20 }} />
                          </Tooltip>
                        </InputAdornment>
                      ) : null,
                    }}
                    helperText={
                      selectedClients.length > 0
                        ? "Click to change clients"
                        : "Click to select clients"
                    }
                  />
                </Tooltip>


              </Grid>

              {/* From Date */}
              <Grid item xs={12} sm={1.6}>
                <Tooltip
                  title="Filter by date received"
                  arrow
                  placement="top"
                >
                  <TextField
                    label="From Date"
                    type="date"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={searchCriteria.startDate}
                    onChange={(e) => setSearchCriteria({ ...searchCriteria, startDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Tooltip>
              </Grid>

              {/* To Date */}
              <Grid item xs={12} sm={1.6}>
                <Tooltip
                  title="Filter by date received"
                  arrow
                  placement="top"
                >
                  <TextField
                    label="To Date"
                    type="date"
                    variant="outlined"
                    fullWidth
                    size="small"
                    value={searchCriteria.endDate}
                    onChange={(e) => setSearchCriteria({ ...searchCriteria, endDate: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                  />
                </Tooltip>
              </Grid>

              {/* Search Button */}
              <Grid item xs={12} sm={1.6}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="medium"
                >
                  Search
                </Button>
              </Grid>

            </Grid>
          </form>

          {/* Display selected statuses and clients as chips */}
          {(getSelectedStatuses().length > 0 || selectedClients.length > 0) && (
            <Box sx={{ mt: 2 }}>
              {/* Selected Statuses */}
              {getSelectedStatuses().length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: '0.875rem' }}>Selected Statuses:</strong>
                    {getSelectedStatuses().map((status) => (
                      <Chip
                        key={status}
                        label={status}
                        onDelete={() => handleRemoveStatus(status)}
                        color="secondary"
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Selected Clients */}
              {selectedClients.length > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: '0.875rem' }}>Selected Clients:</strong>
                    {selectedClients.map((client) => (
                      <Chip
                        key={client.accountNumber}
                        label={`${client.accountNumber} - ${client.clientName}`}
                        onDelete={() => handleRemoveClient(client.accountNumber)}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </div>
      </div>

      {labOrdersData !== null && (
        <LabOrdersTable
          labOrdersData={labOrdersData}
          count={labOrdersCount}
          onChange={onChangePageSettings}
        />
      )}

      <CommonLoader loading={loading} />

      {/* Client Selector Dialog */}
      <ClientSelectorDialog
        open={clientDialogOpen}
        onClose={() => setClientDialogOpen(false)}
        onSelect={handleClientSelect}
        multiSelect={true}
      />
    </div>
  );
};

export default LabOrders;